import type { AxiosInstance } from "axios";
import { EMethod } from "@bc-library/interface";
import { API_MAIN_VERSION, BASE_URL } from "@/config/constantes";
import useAxios from "@/services/api/useAxios";
import type {
  BoardResponse,
  BoardTicket,
  MoveTicketRequest,
  ReorderTicketsRequest,
  Ticket,
  TicketListItem,
  TicketSortBy,
  TicketStatus,
  AddMessageRequest,
  TicketPriority,
  ModuleIdentifier,
  FileUpload,
  ChecklistItem,
  TicketRequestType,
  ActivityFeedResponse,
} from "@/services/ticket/ticketHttp.service";

// Admin-specific message request - admin can tag resources in the firm
export interface AdminAddMessageRequest {
  message: string;
  taggedIds?: string[];
  files?: FileUpload[];
}

// Admin update ticket request
export interface AdminUpdateTicketRequest {
  ticket: {
    display?: string;
    description?: string;
    priority?: TicketPriority;
    deadline?: Date | null;
    officeId?: string | null;
    managedByIds?: string[];
    moduleIdentifier?: ModuleIdentifier | null;
    requestType?: TicketRequestType | null;
  };
  checklist?: ChecklistItem[];
}

// Admin create ticket request
export interface AdminCreateTicketRequest {
  firmId: string;
  ticket: {
    display: string;
    description?: string;
    priority?: TicketPriority;
    deadline?: Date | null;
    officeId?: string | null;
    authorId?: string;
    managedByIds?: string[];
    moduleIdentifier?: ModuleIdentifier | null;
    requestType?: TicketRequestType | null;
  };
  files?: FileUpload[];
  checklist?: ChecklistItem[];
}

// Deadline item for agenda
export interface DeadlineTicket {
  _id: string;
  display: string;
  priority: TicketPriority;
  deadline: string;
  status: TicketStatus;
  firmId: string;
  firmName: string;
}

// Last message item
export interface LastMessageItem {
  _id: string;
  _created: string;
  message: string;
  isAdminMessage: boolean;
  adminAuthor: { name: string; email: string } | null;
  authorName: string | null;
  ticketId: string;
  ticketDisplay: string;
  ticketPriority: TicketPriority;
  ticketStatus: TicketStatus;
  firmId: string;
  firmName: string;
}

export interface LastMessagesResponse {
  docs: LastMessageItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
}

const resource = "tickets";

// Admin-specific types
export interface FirmSummary {
  _id: string;
  company: string;
  isDedicatedServer: boolean;
  hasDedicatedApiUrl?: boolean;
}

export interface AdminTicketStats {
  firm: FirmSummary;
  tickets: Record<string, number>;
  total: number;
  connected: boolean;
  error?: string;
}

export interface AdminBoardResponse {
  firm?: FirmSummary;
  board: BoardResponse;
}

export interface AdminListResponse {
  docs: AdminTicketListItem[];
  count: {
    total: number;
    currentPage: number;
    nextPage: number;
    totalPages: number;
    hasNextPage: boolean;
    limit: number;
  };
  firm?: FirmSummary;
}

// Extended ticket list item for admin that includes firmId
export interface AdminTicketListItem extends TicketListItem {
  archivedAt?: Date;
  firm: FirmSummary;
}

// Resource list item for tagging
export interface AdminResourceListItem {
  _id: string;
  firstname: string;
  lastname: string;
  picture: string | null;
  trigram: string | null;
}

export interface AdminResourceListResponse {
  docs: AdminResourceListItem[];
  count: AdminListResponse["count"];
}

// Office list item
export interface AdminOfficeListItem {
  _id: string;
  display: string;
  countryId: string | null;
}

// Analytics types
export interface DailyServerBreakdown {
  dedicated: number;
  shared: number;
  byPriority: Record<number, number>;
}

export interface DailySeries {
  date: string;
  created: DailyServerBreakdown;
  closed: DailyServerBreakdown;
}

export interface ResolutionByPriority {
  count: number;
  avgMs: number;
}

export interface AdminCloser {
  name: string;
  total: number;
  byPriority: Record<number, number>;
}

export interface AdminAnalyticsResponse {
  year: number;
  month: number;
  dailySeries: DailySeries[];
  resolution: {
    avgClosedMs: number;
    avgOpenMs: number;
    closedCount: number;
    openCount: number;
    byPriority: Record<number, ResolutionByPriority>;
  };
  topAdmins: AdminCloser[];
}

// List all firms for admin selection
const listFirms = (axios: any) => () => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/firms`,
  });
};

// Get ticket stats across all firms
const getStats = (axios: any) => () => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/stats`,
  });
};

// Get analytics data for overview charts
const getAnalytics =
  (axios: any) => (query?: { year?: number; month?: number; firmId?: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/analytics`,
      query,
    });
  };

// Get resources for a specific firm (for tagging/assigning)
const getResources =
  (axios: any) => (query: { firmId: string; search?: string; page?: number; limit?: number }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/resources`,
      query,
    });
  };

// Get offices for a specific firm
const getOffices = (axios: any) => (query: { firmId: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/offices`,
    query,
  });
};

// Get tickets list for a specific firm
const filter =
  (axios: any) =>
  (query: {
    firmId: string;
    page?: number;
    limit?: number;
    search?: string;
    status?: TicketStatus;
    priority?: number[];
    moduleIdentifier?: string[];
    requestType?: TicketRequestType[];
    managedByIds?: string[];
    officeId?: string;
    includeArchived?: boolean;
    sortBy?: TicketSortBy;
  }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/list`,
      query,
    });
  };

// Get board view for a specific firm
const getBoard =
  (axios: any) =>
  (query: {
    firmId: string;
    search?: string;
    moduleIdentifier?: string[];
    requestType?: TicketRequestType[];
    managedByIds?: string[];
    officeId?: string;
    priority?: number[];
    includeArchived?: boolean;
    sortBy?: TicketSortBy;
  }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/board`,
      query,
    });
  };

// Get archived tickets for a specific firm
const getArchived =
  (axios: any) =>
  (query: {
    firmId: string;
    page?: number;
    limit?: number;
    search?: string;
    moduleIdentifier?: string[];
    requestType?: TicketRequestType[];
    managedByIds?: string[];
    officeId?: string;
  }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/archived`,
      query,
    });
  };

// Get single ticket detail
const getById = (axios: any) => (id: string, firmId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${id}`,
    query: { firmId },
  });
};

// Update ticket status
const updateStatus = (axios: any) => (id: string, firmId: string, status: TicketStatus) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${id}/status`,
    data: { firmId, status },
  });
};

// Add message to ticket
const addMessage = (axios: any) => (id: string, firmId: string, data: AdminAddMessageRequest) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${id}/message`,
    data: { firmId, ...data },
  });
};

// Edit admin message (author only)
const editMessage =
  (axios: any) =>
  (
    id: string,
    messageId: string,
    firmId: string,
    data: { message?: string; taggedIds?: string[]; files?: FileUpload[] },
  ) => {
    return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${id}/message/${messageId}`,
      data: { firmId, ...data },
    });
  };

// Delete file from admin message (author only)
const deleteFile =
  (axios: any) => (id: string, messageId: string, fileId: string, firmId: string) => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${id}/message/${messageId}/file/${fileId}`,
      query: { firmId },
    });
  };

export type ImproveMessagePreset = "proofread" | "professional" | "concise" | "translateToEnglish";

export type ImproveMessageRequest = {
  firmId: string;
  message: string;
  preset: ImproveMessagePreset;
  language?: string;
};

export type ImproveMessageResponse = {
  message: string;
};

// Improve draft message with AI presets
const improveMessage = (axios: any) => (data: ImproveMessageRequest) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/improve-message`,
    data,
  });
};

// Update ticket (admin)
const update = (axios: any) => (id: string, firmId: string, data: AdminUpdateTicketRequest) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${id}`,
    data: { firmId, ...data },
  });
};

// Delete ticket (soft delete)
const deleteTicket = (axios: any) => (id: string, firmId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${id}`,
    query: { firmId },
  });
};

// Reorder tickets within a status column (custom sort mode)
const reorder = (axios: any) => (firmId: string, data: ReorderTicketsRequest) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/reorder`,
    data: { firmId, ...data },
  });
};

// Move a ticket (change status and/or order)
const move = (axios: any) => (id: string, firmId: string, data: MoveTicketRequest) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/${id}/move`,
    data: { firmId, ...data },
  });
};

// Create a ticket for a client firm
const createTicket = (axios: any) => (data: AdminCreateTicketRequest) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/create`,
    data,
  });
};

// Get ticket deadlines for a given month
const getDeadlines = (axios: any) => (query: { year: number; month: number }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/deadlines`,
    query,
  });
};

// Get last posted messages across all firms (paginated)
const getLastMessages = (axios: any) => (query: { page?: number; limit?: number }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/last-messages`,
    query,
  });
};

// Get activity feed (tickets created + messages)
const getActivityFeed =
  (axios: any) => (query: { firmId?: string; page?: number; limit?: number; types?: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/admin/activity-feed`,
      query,
    });
  };

export default (axios: AxiosInstance) => ({
  listFirms: listFirms(useAxios(axios)),
  getStats: getStats(useAxios(axios)),
  getAnalytics: getAnalytics(useAxios(axios)),
  getResources: getResources(useAxios(axios)),
  getOffices: getOffices(useAxios(axios)),
  filter: filter(useAxios(axios)),
  getBoard: getBoard(useAxios(axios)),
  getArchived: getArchived(useAxios(axios)),
  getById: getById(useAxios(axios)),
  updateStatus: updateStatus(useAxios(axios)),
  addMessage: addMessage(useAxios(axios)),
  editMessage: editMessage(useAxios(axios)),
  deleteFile: deleteFile(useAxios(axios)),
  improveMessage: improveMessage(useAxios(axios)),
  update: update(useAxios(axios)),
  deleteTicket: deleteTicket(useAxios(axios)),
  reorder: reorder(useAxios(axios)),
  move: move(useAxios(axios)),
  createTicket: createTicket(useAxios(axios)),
  getDeadlines: getDeadlines(useAxios(axios)),
  getLastMessages: getLastMessages(useAxios(axios)),
  getActivityFeed: getActivityFeed(useAxios(axios)),
});
